export function SellMyApartmentsFast() {
  return (
    <section className="py-20 bg-white" id="sell-fast">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Sell My Apartments Fast - Speed Strategies That Work
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Time is money in real estate. Whether you're facing foreclosure, need quick capital, or want to avoid 
            carrying costs, learn proven strategies to accelerate your apartment building sale.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">Why Speed Matters in Real Estate</h3>
            <p className="text-gray-600 mb-8">
              Every day your apartment building sits on the market costs you money in carrying costs, 
              maintenance, utilities, and opportunity cost of tied-up capital.
            </p>
            
            <div className="bg-red-50 border-l-4 border-red-500 p-6 mb-6">
              <h4 className="text-lg font-semibold text-red-800 mb-3">Monthly Carrying Costs Add Up:</h4>
              <ul className="text-gray-700 space-y-2">
                <li>• Mortgage payments: $3,000-$15,000+</li>
                <li>• Property taxes: $500-$3,000</li>
                <li>• Insurance: $300-$1,500</li>
                <li>• Utilities and maintenance: $200-$1,000</li>
                <li>• Lost opportunity cost: Variable</li>
              </ul>
              <p className="text-red-800 font-medium mt-4">
                Total monthly carrying costs: $4,000-$20,500+
              </p>
            </div>

            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-time-line text-white"></i>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Avoid Foreclosure</h4>
                  <p className="text-gray-600">
                    Foreclosure proceedings can damage your credit for years and result in significant financial losses.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-money-dollar-circle-line text-white"></i>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Free Up Capital</h4>
                  <p className="text-gray-600">
                    Quick sales allow you to reinvest capital in better opportunities or address urgent financial needs.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-stress-line text-white"></i>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Reduce Stress</h4>
                  <p className="text-gray-600">
                    Eliminate the stress and uncertainty of a prolonged sale process with guaranteed closing dates.
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <div>
            <img 
              src="https://public.readdy.ai/ai/img_res/fd30df9a629e82271f39f520d65769c8.jpg"
              alt="Fast apartment building sale concept"
              className="w-full h-96 object-cover rounded-xl shadow-lg"
            />
          </div>
        </div>

        <div className="bg-gray-50 rounded-xl p-12 mb-16">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Fast Sale Timeline Comparison
          </h3>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg p-8 text-center shadow-md">
              <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-turtle-line text-3xl text-white"></i>
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-4">Traditional Sale</h4>
              <div className="text-3xl font-bold text-red-600 mb-4">6-12 Months</div>
              <ul className="text-gray-600 space-y-2 text-sm">
                <li>• Property preparation: 2-4 weeks</li>
                <li>• Marketing period: 3-6 months</li>
                <li>• Negotiation: 2-4 weeks</li>
                <li>• Closing process: 6-12 weeks</li>
                <li>• Risk of deal falling through</li>
              </ul>
            </div>

            <div className="bg-white rounded-lg p-8 text-center shadow-md border-2 border-orange-200">
              <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-rocket-line text-3xl text-white"></i>
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-4">Auction Sale</h4>
              <div className="text-3xl font-bold text-orange-600 mb-4">30-60 Days</div>
              <ul className="text-gray-600 space-y-2 text-sm">
                <li>• Property evaluation: 1 week</li>
                <li>• Marketing period: 3-4 weeks</li>
                <li>• Auction event: 1 day</li>
                <li>• Closing process: 2-4 weeks</li>
                <li>• Guaranteed sale date</li>
              </ul>
            </div>

            <div className="bg-white rounded-lg p-8 text-center shadow-md border-2 border-green-200">
              <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-flashlight-line text-3xl text-white"></i>
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-4">Direct Cash Sale</h4>
              <div className="text-3xl font-bold text-green-600 mb-4">7-30 Days</div>
              <ul className="text-gray-600 space-y-2 text-sm">
                <li>• Property evaluation: 1-2 days</li>
                <li>• Offer presentation: 24-48 hours</li>
                <li>• Contract negotiation: 1-3 days</li>
                <li>• Closing process: 1-3 weeks</li>
                <li>• Guaranteed timeline</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 mb-16">
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Strategies to Accelerate Your Sale</h3>
            <div className="space-y-6">
              <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-blue-500">
                <h4 className="text-lg font-semibold text-blue-800 mb-3">Price Aggressively</h4>
                <p className="text-gray-600">
                  Price 5-10% below market value to attract immediate interest and multiple offers. 
                  The speed often compensates for the slightly lower price.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-green-500">
                <h4 className="text-lg font-semibold text-green-800 mb-3">Target Cash Buyers</h4>
                <p className="text-gray-600">
                  Focus marketing efforts on investors and cash buyers who can close quickly 
                  without financing contingencies or lengthy approval processes.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-purple-500">
                <h4 className="text-lg font-semibold text-purple-800 mb-3">Offer Incentives</h4>
                <p className="text-gray-600">
                  Provide closing cost credits, extended due diligence periods, or other incentives 
                  to make your property more attractive than competing listings.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-orange-500">
                <h4 className="text-lg font-semibold text-orange-800 mb-3">Prepare Documentation</h4>
                <p className="text-gray-600">
                  Have all financial records, rent rolls, and property information organized 
                  and ready to share immediately with serious buyers.
                </p>
              </div>
            </div>
          </div>

          <div>
            <img 
              src="https://readdy.ai/api/search-image?query=Professional%20real%20estate%20documents%20and%20contracts%20organized%20on%20desk%20with%20calculator%20and%20pen%20ready%20for%20fast%20apartment%20building%20sale%20clean%20office%20setting&width=800&height=600&seq=sell-apt-docs-9&orientation=landscape"
              alt="Organized real estate documents for fast sale"
              className="w-full h-96 object-cover rounded-xl shadow-lg mb-6"
            />
            
            <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-6">
              <h4 className="text-lg font-semibold text-yellow-800 mb-3">
                <i className="ri-lightbulb-line mr-2"></i>
                Pro Tip: The 7-Day Sale Strategy
              </h4>
              <p className="text-gray-700">
                For emergency situations, partner with a direct buyer who can evaluate your property 
                and close within 7 days. While the price may be 10-15% below market, you save months 
                of carrying costs and eliminate all uncertainties.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-blue-50 rounded-xl p-12 mb-16">
          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            When Speed is Critical - Emergency Situations
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-md text-center">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-home-heart-line text-2xl text-red-600"></i>
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Foreclosure</h4>
              <p className="text-sm text-gray-600">
                Facing foreclosure proceedings with limited time to sell before auction.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md text-center">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-heart-pulse-line text-2xl text-orange-600"></i>
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Health Crisis</h4>
              <p className="text-sm text-gray-600">
                Medical emergencies requiring immediate access to property equity for treatment.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md text-center">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-scales-3-line text-2xl text-purple-600"></i>
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Divorce</h4>
              <p className="text-sm text-gray-600">
                Court-ordered asset division requiring quick property liquidation and distribution.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md text-center">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-flight-takeoff-line text-2xl text-green-600"></i>
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Relocation</h4>
              <p className="text-sm text-gray-600">
                Job transfer or family situation requiring immediate relocation to another city.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-red-500 to-pink-600 rounded-xl p-12 text-center text-white">
          <h3 className="text-3xl font-bold mb-4">
            Emergency Sale? We Can Close in 7 Days
          </h3>
          <p className="text-xl mb-8 max-w-4xl mx-auto">
            When you need to sell your apartment building immediately, we have the experience and capital 
            to close in as little as 7 days. No waiting, no uncertainty, no complications.
          </p>
          <div className="grid md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <div className="text-2xl font-bold mb-1">24 Hours</div>
              <div className="text-sm">Offer Response</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <div className="text-2xl font-bold mb-1">3 Days</div>
              <div className="text-sm">Due Diligence</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <div className="text-2xl font-bold mb-1">7 Days</div>
              <div className="text-sm">Closing</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <div className="text-2xl font-bold mb-1">Cash</div>
              <div className="text-sm">In Hand</div>
            </div>
          </div>
          <button className="bg-white text-red-600 px-12 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 whitespace-nowrap">
            Get Emergency Sale Quote
          </button>
        </div>
      </div>
    </section>
  );
}